import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { TIME_SLOTS_24H, MOCK_ACOUSTIC_INDICES, DARK_THEME_EXPORTING, DARK_THEME_NAVIGATION } from '../bioacousticMockData';

interface AcousticIndicesChartProps {
  deploymentName?: string;
  height?: number;
}

const INDEX_CONFIG = [
  { key: 'aci', label: 'ACI', name: 'Acoustic Complexity Index', color: '#00D4FF', symbol: 'circle' },
  { key: 'bi', label: 'BI', name: 'Bioacoustic Index', color: '#F59E0B', symbol: 'diamond' },
  { key: 'adi', label: 'ADI', name: 'Acoustic Diversity Index', color: '#10B981', symbol: 'square' },
  { key: 'aei', label: 'AEI', name: 'Acoustic Evenness Index', color: '#EF4444', symbol: 'triangle' },
  { key: 'ndsi', label: 'NDSI', name: 'Normalized Difference Soundscape Index', color: '#8B5CF6', symbol: 'triangle-down' },
];

const AcousticIndicesChart: React.FC<AcousticIndicesChartProps> = ({
  deploymentName = 'Baseline Station',
  height = 340,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<string>('all');

  const chartOptions: Highcharts.Options = useMemo(() => {
    const series: Highcharts.SeriesOptionsType[] =
      selectedIndex === 'all'
        ? INDEX_CONFIG.map((idx) => ({
            type: 'spline',
            name: idx.label,
            color: idx.color,
            data: MOCK_ACOUSTIC_INDICES[idx.key as keyof typeof MOCK_ACOUSTIC_INDICES] || [],
            marker: {
              symbol: idx.symbol,
              radius: 4,
              fillColor: idx.color,
            },
          }))
        : (() => {
            const current = INDEX_CONFIG.find((i) => i.key === selectedIndex) || INDEX_CONFIG[0];
            return [
              {
                type: 'spline',
                name: current.label,
                color: current.color,
                data: MOCK_ACOUSTIC_INDICES[current.key as keyof typeof MOCK_ACOUSTIC_INDICES] || [],
                marker: {
                  symbol: current.symbol,
                  radius: 4,
                  fillColor: current.color,
                },
              },
            ];
          })();

    return {
      chart: {
        type: 'spline',
        backgroundColor: 'transparent',
        height,
        style: {
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
        },
      },
      exporting: DARK_THEME_EXPORTING,
      navigation: DARK_THEME_NAVIGATION,
      title: {
        text: 'Index wise Activity',
        align: 'center',
        style: {
          color: '#FFFFFF',
          fontSize: '15px',
          fontWeight: '700',
          letterSpacing: '0.5px',
        },
      },
      credits: { enabled: false },
      xAxis: {
        categories: TIME_SLOTS_24H,
        title: {
          text: 'Time of Day',
          style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
        },
        labels: {
          rotation: -45,
          style: { color: '#888888', fontSize: '10px' },
        },
        lineColor: '#222222',
        tickColor: '#222222',
        gridLineColor: 'rgba(255, 255, 255, 0.03)',
        crosshair: {
          color: 'rgba(16, 185, 129, 0.3)',
          dashStyle: 'Dash' as any,
        },
      },
      yAxis: {
        title: {
          text: selectedIndex === 'all' ? 'Normalized Value (0–1)' : 'Acoustic Index Value',
          style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
        },
        labels: {
          style: { color: '#888888', fontSize: '10px' },
        },
        min: 0,
        max: 1.25,
        tickPositions: [0, 0.25, 0.5, 0.75, 1, 1.25],
        gridLineColor: '#1A1A1A',
      },
      tooltip: {
        backgroundColor: '#111111',
        borderColor: '#222222',
        style: { color: '#EEEEEE', fontSize: '11px' },
        shared: true,
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          color: '#999999',
          fontSize: '11px',
          fontWeight: '600',
        },
        itemHoverStyle: {
          color: '#FFFFFF',
        },
      },
      series,
    };
  }, [selectedIndex, height]);

  return (
    <Box
      sx={{
        background: 'rgba(10, 10, 10, 0.85)',
        border: '1px solid #1E1E1E',
        borderRadius: '12px',
        p: 2.5,
      }}
    >
      {/* Header bar */}
      <Box sx={{ borderBottom: '1px solid #1A1A1A', pb: 1.5, mb: 2 }}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 800,
            color: '#10B981',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            mb: 1,
          }}
        >
          Acoustic Indices Analysis
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#CCCCCC' }}>
            {deploymentName}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              variant="standard"
              disableUnderline
              size="small"
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(e.target.value)}
              sx={{
                color: '#10B981',
                fontWeight: 700,
                fontSize: '13px',
                borderBottom: '2px solid #10B981',
                '& .MuiSelect-select': {
                  py: 0.25,
                  px: 1,
                },
                '& .MuiSvgIcon-root': {
                  color: '#10B981',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#111',
                    border: '1px solid #222',
                    color: '#fff',
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981' },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="aci">ACI</MenuItem>
              <MenuItem value="bi">BI</MenuItem>
              <MenuItem value="adi">ADI</MenuItem>
              <MenuItem value="aei">AEI</MenuItem>
              <MenuItem value="ndsi">NDSI</MenuItem>
            </Select>

            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#10B981', mb: 0.5 }}>
                    ACOUSTIC SOUNDSCAPE INDICES
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#DDD', mb: 0.25 }}>
                    <b>ACI:</b> Acoustic Complexity Index (biological sound complexity)
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#DDD', mb: 0.25 }}>
                    <b>BI:</b> Bioacoustic Index (biophony energy in 2-8kHz band)
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#DDD', mb: 0.25 }}>
                    <b>ADI:</b> Acoustic Diversity Index (evenness across bands)
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#DDD', mb: 0.25 }}>
                    <b>AEI:</b> Acoustic Evenness Index (sound concentration)
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#DDD' }}>
                    <b>NDSI:</b> Normalized Difference Soundscape (anthrophony vs biophony)
                  </Typography>
                </Box>
              }
              arrow
              placement="left"
            >
              <IconButton size="small" sx={{ color: '#666', '&:hover': { color: '#10B981' } }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Highcharts chart */}
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </Box>
  );
};

export default AcousticIndicesChart;
